import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity, Users, LogOut, AlertTriangle, ChevronDown, ChevronRight, ChevronLeft,
  Radio, Clock, CalendarDays, PieChart as PieIcon, UserX,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, BarChart, Bar, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { JaritBadge } from "@/components/JaritBadge";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { useQuickProfile } from "@/context/QuickProfileContext";
import { useT } from "@/i18n";

const PRESENCE_LABEL = {
  en: {
    NOT_ARRIVED: "Not Arrived", WORKING: "Working", ON_BREAK: "On Break",
    CLOCKED_OUT: "Clocked Out", NEEDS_REVIEW: "Needs Review",
    NOT_WORKING: "0 Day / Not Working", IGNORED: "Ignored",
  },
  id: {
    NOT_ARRIVED: "Belum Datang", WORKING: "Sedang Bekerja", ON_BREAK: "Istirahat",
    CLOCKED_OUT: "Sudah Pulang", NEEDS_REVIEW: "Perlu Ditinjau",
    NOT_WORKING: "0 Hari / Tidak Masuk", IGNORED: "Diabaikan",
  },
  ko: {
    NOT_ARRIVED: "미출근", WORKING: "근무 중", ON_BREAK: "휴식",
    CLOCKED_OUT: "퇴근", NEEDS_REVIEW: "검토 필요", NOT_WORKING: "0일 / 결근", IGNORED: "무시됨",
  },
  ja: {
    NOT_ARRIVED: "未出勤", WORKING: "勤務中", ON_BREAK: "休憩",
    CLOCKED_OUT: "退勤", NEEDS_REVIEW: "要確認", NOT_WORKING: "0日 / 欠勤", IGNORED: "無視",
  },
};
const PRESENCE_STYLE = {
  WORKING: "bg-[#E9F9EF] text-[#0E8A46]",
  CLOCKED_OUT: "bg-[#F1F1F4] text-[#6E6E73]",
  NEEDS_REVIEW: "bg-[#FFF1E6] text-[#B5590F]",
  NOT_ARRIVED: "bg-[#F1F1F4] text-[#8E8E93]",
  NOT_WORKING: "bg-[#FDECEC] text-[#C0362C]",
  IGNORED: "bg-[#F1F1F4] text-[#8E8E93]",
};
const RESULT_LABEL = {
  en: {
    FULL_DAY: "Full Day", HALF_DAY_AM: "Half Day", HALF_DAY_PM: "Half Day",
    HALF_DAY: "Half Day", NEEDS_REVIEW: "Needs Review",
    ZERO: "0 Day — Manual", IGNORED: "Ignored",
  },
  id: {
    FULL_DAY: "Hari Penuh", HALF_DAY_AM: "Setengah Hari", HALF_DAY_PM: "Setengah Hari",
    HALF_DAY: "Setengah Hari", NEEDS_REVIEW: "Perlu Ditinjau",
    ZERO: "0 Hari — Manual", IGNORED: "Diabaikan",
  },
  ko: {
    FULL_DAY: "종일", HALF_DAY_AM: "반일", HALF_DAY_PM: "반일",
    HALF_DAY: "반일", NEEDS_REVIEW: "검토 필요", ZERO: "0일 — 수동", IGNORED: "무시됨",
  },
  ja: {
    FULL_DAY: "終日", HALF_DAY_AM: "半日", HALF_DAY_PM: "半日",
    HALF_DAY: "半日", NEEDS_REVIEW: "要確認", ZERO: "0日 — 手動", IGNORED: "無視",
  },
};
const SCAN_KIND = {
  en: {
    VALID_IN: ["Clock In", "text-[#0E8A46]"], VALID_OUT: ["Clock Out", "text-[#0058C7]"],
    DUPLICATE_IGNORED: ["Duplicate — Ignored", "text-[#A1A1A6]"],
    REPEATED_ARRIVAL_IGNORED: ["Repeated Arrival — Ignored", "text-[#A1A1A6]"],
    EXTRA_IGNORED: ["Extra Scan — Ignored", "text-[#A1A1A6]"],
    MANUAL_IGNORED: ["Ignored (manual)", "text-[#A1A1A6]"],
  },
  id: {
    VALID_IN: ["Jam Masuk", "text-[#0E8A46]"], VALID_OUT: ["Jam Keluar", "text-[#0058C7]"],
    DUPLICATE_IGNORED: ["Duplikat — Diabaikan", "text-[#A1A1A6]"],
    REPEATED_ARRIVAL_IGNORED: ["Datang Berulang — Diabaikan", "text-[#A1A1A6]"],
    EXTRA_IGNORED: ["Scan Ekstra — Diabaikan", "text-[#A1A1A6]"],
    MANUAL_IGNORED: ["Diabaikan (manual)", "text-[#A1A1A6]"],
  },
  ko: {
    VALID_IN: ["출근", "text-[#0E8A46]"], VALID_OUT: ["퇴근", "text-[#0058C7]"],
    DUPLICATE_IGNORED: ["중복 — 무시", "text-[#A1A1A6]"],
    REPEATED_ARRIVAL_IGNORED: ["반복 출근 — 무시", "text-[#A1A1A6]"],
    EXTRA_IGNORED: ["추가 스캔 — 무시", "text-[#A1A1A6]"],
    MANUAL_IGNORED: ["무시 (수동)", "text-[#A1A1A6]"],
  },
  ja: {
    VALID_IN: ["出勤", "text-[#0E8A46]"], VALID_OUT: ["退勤", "text-[#0058C7]"],
    DUPLICATE_IGNORED: ["重複 — 無視", "text-[#A1A1A6]"],
    REPEATED_ARRIVAL_IGNORED: ["繰返し出勤 — 無視", "text-[#A1A1A6]"],
    EXTRA_IGNORED: ["追加スキャン — 無視", "text-[#A1A1A6]"],
    MANUAL_IGNORED: ["無視 (手動)", "text-[#A1A1A6]"],
  },
};
const REASON_LABEL = {
  en: {
    MISSING_PAIR: "Missing Clock-In/Clock-Out Pair", LATE_ARRIVAL: "Late Arrival",
    EARLY_DEPARTURE: "Early Departure", AMBIGUOUS_HALF_DAY: "Ambiguous Half Day",
    UNUSUAL_SCAN_PATTERN: "Unusual Scan Pattern", NEW_SCAN_AFTER_REVIEW: "New Scan After Review",
  },
  id: {
    MISSING_PAIR: "Pasangan Masuk/Keluar Tidak Lengkap", LATE_ARRIVAL: "Datang Terlambat",
    EARLY_DEPARTURE: "Pulang Lebih Awal", AMBIGUOUS_HALF_DAY: "Setengah Hari Ambigu",
    UNUSUAL_SCAN_PATTERN: "Pola Scan Tidak Wajar", NEW_SCAN_AFTER_REVIEW: "Scan Baru Setelah Ditinjau",
  },
  ko: {
    MISSING_PAIR: "출근/퇴근 짝 누락", LATE_ARRIVAL: "지각",
    EARLY_DEPARTURE: "조퇴", AMBIGUOUS_HALF_DAY: "모호한 반일",
    UNUSUAL_SCAN_PATTERN: "비정상 스캔 패턴", NEW_SCAN_AFTER_REVIEW: "검토 후 새 스캔",
  },
  ja: {
    MISSING_PAIR: "出勤/退勤ペア欠落", LATE_ARRIVAL: "遅刻",
    EARLY_DEPARTURE: "早退", AMBIGUOUS_HALF_DAY: "曖昧な半日",
    UNUSUAL_SCAN_PATTERN: "異常なスキャンパターン", NEW_SCAN_AFTER_REVIEW: "確認後の新しいスキャン",
  },
};

const STR = {
  en: {
    liveAtt: "Live Attendance", autoUpdating: " · auto-updating", viewAtt: "View Attendance",
    beforeGolive: "Before Go-Live", liveStarts: "Live attendance monitoring starts on",
    working: "Currently Working", present: "Present Today", clockedOut: "Clocked Out",
    notArrived: "Not Arrived", needsReview: "Needs Review", notWorking: "0 Day / Not Working",
    noneWorking: "No employees are currently working.", viewAll: "View All", showLess: "Show Less",
    flowTitle: "Today's Workforce Flow", flowSub: "Employees currently working through the day.",
    flowEmptyGolive: "No activity before go-live.", flowEmpty: "No workforce activity yet today.",
    attTitle: "Today's Attendance", attSub: "Tap a segment or a legend row to see employees.", attPast: "Attendance breakdown for the selected day.",
    beforeGoliveShort: "Before\nGo-Live", noData: "No data",
    trendTitle: "7-Day Attendance Trend", trendSub: "Present · Full Day · Half Day · Needs Review across recent operating days.",
    trendGolive: "Trend starts on go-live.", trendEmpty: "No attendance recorded yet.",
    search: "Search name or NIP…", noEmp: "No employees.", reviewAtt: "Review Attendance",
    clockIn: "Clock In", latest: "Latest", clockOut: "Clock Out", result: "Result", inProgress: "In progress",
    notRecorded: "Not Recorded", todaysScans: "Today's Scans", pieceRate: "Piece-Rate",
    manualResolved: "Attendance manually resolved · clock-out not recorded",
    clockedInAt: "Clocked in at", latestLbl: "Latest", clockoutManual: "Clock-out not recorded (manual)",
    legPresent: "Present", legFull: "Full Day", legHalf: "Half Day", legNeeds: "Needs Review", tipWorking: "Working",
  },
  id: {
    liveAtt: "Absensi Langsung", autoUpdating: " · diperbarui otomatis", viewAtt: "Lihat Absensi",
    beforeGolive: "Sebelum Go-Live", liveStarts: "Pemantauan absensi langsung dimulai pada",
    working: "Sedang Bekerja", present: "Hadir Hari Ini", clockedOut: "Sudah Pulang",
    notArrived: "Belum Datang", needsReview: "Perlu Ditinjau", notWorking: "0 Hari / Tidak Masuk",
    noneWorking: "Belum ada yang bekerja saat ini.", viewAll: "Lihat Semua", showLess: "Ringkas",
    flowTitle: "Alur Tenaga Kerja Hari Ini", flowSub: "Karyawan yang sedang bekerja sepanjang hari.",
    flowEmptyGolive: "Tidak ada aktivitas sebelum go-live.", flowEmpty: "Belum ada aktivitas tenaga kerja hari ini.",
    attTitle: "Absensi Hari Ini", attSub: "Ketuk segmen atau baris legenda untuk melihat karyawan.", attPast: "Rincian absensi untuk tanggal yang dipilih.",
    beforeGoliveShort: "Sebelum\nGo-Live", noData: "Tidak ada data",
    trendTitle: "Tren Absensi 7 Hari", trendSub: "Hadir · Hari Penuh · Setengah Hari · Perlu Ditinjau pada hari kerja terbaru.",
    trendGolive: "Tren dimulai saat go-live.", trendEmpty: "Belum ada absensi tercatat.",
    search: "Cari nama atau NIP…", noEmp: "Tidak ada karyawan.", reviewAtt: "Tinjau Absensi",
    clockIn: "Jam Masuk", latest: "Terbaru", clockOut: "Jam Keluar", result: "Hasil", inProgress: "Berjalan",
    notRecorded: "Tidak Tercatat", todaysScans: "Scan Hari Ini", pieceRate: "Borongan",
    manualResolved: "Absensi diselesaikan manual · jam keluar tidak tercatat",
    clockedInAt: "Masuk pukul", latestLbl: "Terbaru", clockoutManual: "Jam keluar tidak tercatat (manual)",
    legPresent: "Hadir", legFull: "Hari Penuh", legHalf: "Setengah Hari", legNeeds: "Perlu Ditinjau", tipWorking: "Bekerja",
  },
  ko: {
    liveAtt: "실시간 출근", autoUpdating: " · 자동 업데이트", viewAtt: "출근 보기",
    beforeGolive: "시작 전", liveStarts: "실시간 출근 모니터링 시작일:",
    working: "현재 근무 중", present: "오늘 출근", clockedOut: "퇴근",
    notArrived: "미출근", needsReview: "검토 필요", notWorking: "0일 / 결근",
    noneWorking: "현재 근무 중인 직원이 없습니다.", viewAll: "모두 보기", showLess: "접기",
    flowTitle: "오늘의 근무 흐름", flowSub: "하루 동안 근무 중인 직원.",
    flowEmptyGolive: "시작 전 활동 없음.", flowEmpty: "오늘 아직 활동이 없습니다.",
    attTitle: "오늘의 출근", attSub: "세그먼트나 범례를 눌러 직원을 확인하세요.", attPast: "선택한 날짜의 출근 현황.",
    beforeGoliveShort: "시작\n전", noData: "데이터 없음",
    trendTitle: "7일 출근 추이", trendSub: "최근 운영일의 출근 · 종일 · 반일 · 검토 필요.",
    trendGolive: "시작일부터 추이가 표시됩니다.", trendEmpty: "아직 출근 기록이 없습니다.",
    search: "이름 또는 NIP 검색…", noEmp: "직원이 없습니다.", reviewAtt: "출근 검토",
    clockIn: "출근", latest: "최근", clockOut: "퇴근", result: "결과", inProgress: "진행 중",
    notRecorded: "미기록", todaysScans: "오늘의 스캔", pieceRate: "성과급",
    manualResolved: "출근 수동 처리 · 퇴근 미기록",
    clockedInAt: "출근 시각", latestLbl: "최근", clockoutManual: "퇴근 미기록 (수동)",
    legPresent: "출근", legFull: "종일", legHalf: "반일", legNeeds: "검토 필요", tipWorking: "근무",
  },
  ja: {
    liveAtt: "リアルタイム勤怠", autoUpdating: " · 自動更新", viewAtt: "勤怠を見る",
    beforeGolive: "開始前", liveStarts: "リアルタイム勤怠監視の開始日:",
    working: "勤務中", present: "本日出勤", clockedOut: "退勤",
    notArrived: "未出勤", needsReview: "要確認", notWorking: "0日 / 欠勤",
    noneWorking: "現在勤務中の従業員はいません。", viewAll: "すべて表示", showLess: "折りたたむ",
    flowTitle: "本日の勤務フロー", flowSub: "日中に勤務中の従業員。",
    flowEmptyGolive: "開始前の活動なし。", flowEmpty: "本日はまだ活動がありません。",
    attTitle: "本日の勤怠", attSub: "セグメントや凡例をタップして従業員を表示。", attPast: "選択した日の勤怠内訳。",
    beforeGoliveShort: "開始\n前", noData: "データなし",
    trendTitle: "7日間の勤怠推移", trendSub: "直近の稼働日の 出勤 · 終日 · 半日 · 要確認。",
    trendGolive: "開始日から推移が表示されます。", trendEmpty: "まだ勤怠記録がありません。",
    search: "名前またはNIPで検索…", noEmp: "従業員がいません。", reviewAtt: "勤怠を確認",
    clockIn: "出勤", latest: "最新", clockOut: "退勤", result: "結果", inProgress: "進行中",
    notRecorded: "未記録", todaysScans: "本日のスキャン", pieceRate: "出来高",
    manualResolved: "勤怠を手動処理 · 退勤未記録",
    clockedInAt: "出勤時刻", latestLbl: "最新", clockoutManual: "退勤未記録 (手動)",
    legPresent: "出勤", legFull: "終日", legHalf: "半日", legNeeds: "要確認", tipWorking: "勤務",
  },
};

const EN_MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];
const ID_MONTHS = ["Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
const fmtGoLive = (iso, id) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const M = id ? ID_MONTHS : EN_MONTHS;
  return id ? `${d} ${M[m - 1]} ${y}` : `${M[m - 1]} ${d}, ${y}`;
};

const KO_MONTHS = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
const JA_MONTHS = Array.from({ length: 12 }, (_, i) => `${i + 1}月`);
const CAL = {
  en: { months: EN_MONTHS, wd: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"], title: "Attendance Calendar", sub: "Pick a date to view its attendance." },
  id: { months: ID_MONTHS, wd: ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"], title: "Kalender Absensi", sub: "Pilih tanggal untuk melihat absensinya." },
  ko: { months: KO_MONTHS, wd: ["월", "화", "수", "목", "금", "토", "일"], title: "출근 캘린더", sub: "날짜를 선택해 출근 현황을 확인하세요." },
  ja: { months: JA_MONTHS, wd: ["月", "火", "水", "木", "金", "土", "日"], title: "勤怠カレンダー", sub: "日付を選んで勤怠を表示。" },
};

const SummaryCard = ({ icon: Icon, label, value, color, onClick, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className="liquid-glass-card flex items-center gap-3 p-4 text-left transition-all hover:bg-white/70 active:scale-[0.98]"
  >
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full" style={{ background: `${color}1A`, color }}>
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <p className="num text-[24px] font-bold leading-none">{value}</p>
      <p className="mt-1 text-[12px] text-[#8E8E93]">{label}</p>
    </div>
  </button>
);

// Stacked employee card used inside detail sheets and previews.
const EmpCard = ({ r, mode, onReview, t, lng }) => {
  const { openQuickProfile } = useQuickProfile();
  const resultLabel = (c) => RESULT_LABEL[lng][c] || (c ? c.replace(/_/g, " ") : "—");
  const reasonLabel = (c) => (c ? REASON_LABEL[lng][c] || c.replace(/_/g, " ") : "");
  return (
  <div className="rounded-2xl border border-black/[0.05] bg-white/70 p-3.5">
    <div className="flex items-start justify-between gap-2">
      <button onClick={() => openQuickProfile(r.employee_id)} className="flex min-w-0 items-center gap-2.5 text-left">
        <EmployeeAvatar id={r.employee_id} nama={r.nama} hasPhoto={r.has_photo} size={36} />
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[14px] font-semibold text-[#1D1D1F] hover:text-[#0071E3]">
            <span className="truncate">{r.nama}</span>
            {r.verified && r.payroll_type !== "PIECE_RATE" && <VerifiedBadge size={15} />}
            <JaritBadge show={r.payroll_type === "PIECE_RATE"} size={16} />
          </p>
          <p className="num text-[12px] text-[#8E8E93]">{r.nip || "—"}</p>
        </div>
      </button>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-[11.5px] font-medium ${PRESENCE_STYLE[r.presence] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
        {PRESENCE_LABEL[lng][r.presence] || r.presence}
      </span>
    </div>
    <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
      <div className="rounded-xl bg-black/[0.03] py-2">
        <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">{t.clockIn}</p>
        <p className="num mt-0.5 text-[13px] font-semibold">{r.first_scan || "—"}</p>
      </div>
      <div className="rounded-xl bg-black/[0.03] py-2">
        <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">{r.manual_closed ? t.clockOut : t.latest}</p>
        <p className="num mt-0.5 text-[13px] font-semibold">{r.manual_closed ? t.notRecorded : (r.last_scan || "—")}</p>
      </div>
      <div className="rounded-xl bg-black/[0.03] py-2">
        <p className="text-[10px] uppercase tracking-wide text-[#8E8E93]">{t.result}</p>
        <p className="mt-0.5 text-[12.5px] font-semibold">{r.presence === "WORKING" ? t.inProgress : resultLabel(r.attendance_result)}</p>
      </div>
    </div>
    {r.manual_closed && (
      <p className="mt-2 text-[11.5px] font-medium text-[#0058C7]">{t.manualResolved}</p>
    )}
    {mode === "review" && r.reason_code && (
      <p className="mt-2 text-[11.5px] font-medium text-[#B5590F]">{reasonLabel(r.reason_code)}</p>
    )}
    {(r.scan_events || []).length > 0 && (
      <div className="mt-2.5">
        <p className="mb-1 text-[11px] font-medium text-[#8E8E93]">{t.todaysScans}</p>
        <div className="space-y-1">
          {r.scan_events.map((s, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-black/[0.03] px-2.5 py-1.5">
              <span className="num text-[12.5px] font-medium text-[#1D1D1F]">{s.t}</span>
              <span className={`text-[12px] ${(SCAN_KIND[lng][s.kind] || ["", "text-[#8E8E93]"])[1]}`}>
                {(SCAN_KIND[lng][s.kind] || [s.kind])[0]}
              </span>
            </div>
          ))}
        </div>
      </div>
    )}
    {mode === "review" && (
      <button
        data-testid="live-review-attendance"
        onClick={onReview}
        className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-full bg-[#0071E3] py-2.5 text-[13px] font-medium text-white transition-all hover:bg-[#0077ED] active:scale-[0.98]"
      >
        <Radio className="h-4 w-4" /> {t.reviewAtt}
      </button>
    )}
  </div>
  );
};

const DetailSheet = ({ open, onClose, title, color, rows, mode, onReview, t, lng }) => {
  const [q, setQ] = useState("");
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const ql = q.trim().toLowerCase();
  const filtered = ql
    ? rows.filter((r) => (r.nama || "").toLowerCase().includes(ql) || (r.nip || "").toLowerCase().includes(ql))
    : rows;
  return (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent
      data-testid="live-detail-sheet"
      onOpenAutoFocus={(e) => e.preventDefault()}
      className="flex max-h-[85vh] flex-col gap-0 rounded-[22px] border border-white/50 bg-white/80 p-0 shadow-[0_20px_60px_rgba(0,0,0,0.2)] ring-1 ring-inset ring-white/50 backdrop-blur-2xl backdrop-saturate-150 sm:max-w-[520px]"
    >
      <DialogHeader className="border-b border-black/[0.06] px-5 py-4 text-left">
        <DialogTitle className="flex items-center gap-2 pr-9 text-[17px] font-semibold tracking-tight">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />
          {title}
          <span className="num ml-auto rounded-full bg-black/[0.05] px-2.5 py-0.5 text-[13px] font-bold">{filtered.length}</span>
        </DialogTitle>
      </DialogHeader>
      <div className="border-b border-black/[0.06] px-4 py-2.5">
        <input
          data-testid="live-detail-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t.search}
          className="h-9 w-full rounded-lg border border-[#E1E1E6] bg-white/70 px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-[#0071E3]/30"
        />
      </div>
      <div className="flex-1 space-y-2.5 overflow-y-auto px-4 py-4">
        {filtered.length === 0 ? (
          <p className="py-12 text-center text-[13.5px] text-[#8E8E93]">{t.noEmp}</p>
        ) : (
          filtered.map((r) => (
            <EmpCard key={r.employee_id} r={r} mode={mode} onReview={() => onReview(r)} t={t} lng={lng} />
          ))
        )}
      </div>
    </DialogContent>
  </Dialog>
  );
};

const PreviewRow = ({ r, expanded, onToggle, t, lng }) => {
  const { openQuickProfile } = useQuickProfile();
  return (
  <div className="border-b border-black/[0.04] py-2.5 last:border-0">
    <button onClick={onToggle} className="flex w-full items-center justify-between gap-2 text-left">
      <div className="flex min-w-0 items-center gap-2.5">
        <span onClick={(e) => { e.stopPropagation(); r.employee_id && openQuickProfile(r.employee_id); }} className="cursor-pointer" data-testid={`live-avatar-${r.employee_id}`}>
          <EmployeeAvatar id={r.employee_id} nama={r.nama} hasPhoto={r.has_photo} size={32} />
        </span>
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[13px] font-medium text-[#1D1D1F]">
            <span className="truncate">{r.nama}</span>
            {r.verified && r.payroll_type !== "PIECE_RATE" && <VerifiedBadge size={14} />}
            <JaritBadge show={r.payroll_type === "PIECE_RATE"} size={15} />
          </p>
          <p className="num text-[11.5px] text-[#8E8E93]">
            {r.nip || "—"} · {t.clockedInAt} {r.first_scan || "—"} · {r.manual_closed ? t.clockoutManual : `${t.latestLbl} ${r.last_scan || "—"}`}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${PRESENCE_STYLE[r.presence] || "bg-[#F1F1F4] text-[#6E6E73]"}`}>
          {PRESENCE_LABEL[lng][r.presence] || r.presence}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-[#8E8E93] transition-transform ${expanded ? "rotate-180" : ""}`} />
      </div>
    </button>
    {expanded && (
      <div className="mt-1.5 space-y-1 pl-0.5">
        <span className="text-[11px] text-[#8E8E93]">{t.todaysScans}</span>
        {(r.scan_events || []).length ? r.scan_events.map((s, i) => (
          <div key={i} className="flex items-center justify-between rounded-lg bg-black/[0.03] px-2.5 py-1.5">
            <span className="num text-[12px] font-medium">{s.t}</span>
            <span className={`text-[11.5px] ${(SCAN_KIND[lng][s.kind] || ["", "text-[#8E8E93]"])[1]}`}>
              {(SCAN_KIND[lng][s.kind] || [s.kind])[0]}
            </span>
          </div>
        )) : <span className="text-[11px] text-[#8E8E93]">—</span>}
      </div>
    )}
  </div>
  );
};

const DONUT_COLORS = { working: "#0E8A46", clocked: "#8E8E93", not_arrived: "#C7C7CC", needs: "#B5590F", not_working: "#C0362C" };

export default function LiveAttendance({ showReviewNav = true }) {
  const { lang } = useT();
  const lng = STR[lang] ? lang : "en";
  const t = STR[lng];
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [exp, setExp] = useState({});
  const [detail, setDetail] = useState(null); // {key,title,color,rows,mode}

  const load = useCallback(async () => {
    try { setData((await api.get("/attendance/live")).data); } catch { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const int = setInterval(load, 12000);
    return () => clearInterval(int);
  }, [load]);

  const before = !!data?.before_golive;
  const s = data?.summary || { currently_working: 0, present_today: 0, clocked_out: 0, needs_review: 0 };
  const working = data?.working || [];
  const present = data?.present || [];
  const clockedOut = data?.clocked_out || [];
  const needsReview = data?.needs_review || [];
  const notArrived = data?.not_arrived || [];
  const notWorking = data?.not_working || [];
  const trend = data?.trend || [];
  const clockedPure = useMemo(() => clockedOut.filter((r) => r.presence === "CLOCKED_OUT"), [clockedOut]);

  const donutData = useMemo(() => ([
    { key: "working", name: t.working, value: s.currently_working, rows: working, color: DONUT_COLORS.working },
    { key: "clocked", name: t.clockedOut, value: clockedPure.length, rows: clockedPure, color: DONUT_COLORS.clocked },
    { key: "not_working", name: t.notWorking, value: notWorking.length, rows: notWorking, color: DONUT_COLORS.not_working },
    { key: "not_arrived", name: t.notArrived, value: notArrived.length, rows: notArrived, color: DONUT_COLORS.not_arrived },
    { key: "needs", name: t.needsReview, value: s.needs_review, rows: needsReview, color: DONUT_COLORS.needs },
  ]), [s, working, clockedPure, notArrived, notWorking, needsReview, t]);
  const donutTotal = donutData.reduce((a, b) => a + b.value, 0);

  const todayStr = useMemo(() => new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10), []);
  const [selectedDate, setSelectedDate] = useState(todayStr);
  const [dayData, setDayData] = useState(null);
  const isToday = selectedDate === todayStr;
  const [calCursor, setCalCursor] = useState(() => { const [y, m] = todayStr.split("-").map(Number); return { y, m }; });
  const cal = CAL[lng] || CAL.en;

  useEffect(() => {
    if (isToday) { setDayData(null); return; }
    let active = true;
    api.get("/attendance/live", { params: { date: selectedDate } })
      .then((r) => { if (active) setDayData(r.data); }).catch(() => {});
    return () => { active = false; };
  }, [selectedDate, isToday]);

  const dd = isToday ? data : dayData;
  const dClockedPure = (dd?.clocked_out || []).filter((r) => r.presence === "CLOCKED_OUT");
  const dSum = dd?.summary || { currently_working: 0, needs_review: 0 };
  const HALF_RESULTS = ["HALF_DAY", "HALF_DAY_AM", "HALF_DAY_PM"];
  const dPastFull = dClockedPure.filter((r) => r.attendance_result === "FULL_DAY");
  const dPastHalf = dClockedPure.filter((r) => HALF_RESULTS.includes(r.attendance_result));
  const dPastAbsent = [...(dd?.not_arrived || []), ...(dd?.not_working || [])];
  const dayDonutData = isToday ? [
    { key: "working", name: t.working, value: dSum.currently_working || 0, rows: dd?.working || [], color: DONUT_COLORS.working },
    { key: "clocked", name: t.clockedOut, value: dClockedPure.length, rows: dClockedPure, color: DONUT_COLORS.clocked },
    { key: "not_working", name: t.notWorking, value: (dd?.not_working || []).length, rows: dd?.not_working || [], color: DONUT_COLORS.not_working },
    { key: "not_arrived", name: t.notArrived, value: (dd?.not_arrived || []).length, rows: dd?.not_arrived || [], color: DONUT_COLORS.not_arrived },
    { key: "needs", name: t.needsReview, value: dSum.needs_review || 0, rows: dd?.needs_review || [], color: DONUT_COLORS.needs },
  ] : [
    { key: "present", name: t.legPresent, value: dPastFull.length, rows: dPastFull, color: DONUT_COLORS.working },
    { key: "half", name: t.legHalf, value: dPastHalf.length, rows: dPastHalf, color: "#E8820E" },
    { key: "not_arrived", name: t.notArrived, value: dPastAbsent.length, rows: dPastAbsent, color: DONUT_COLORS.not_arrived },
    { key: "needs", name: t.needsReview, value: dSum.needs_review || 0, rows: dd?.needs_review || [], color: DONUT_COLORS.needs },
  ];
  const dayDonutTotal = dayDonutData.reduce((a, b) => a + b.value, 0);
  const openDayDetail = (key) => {
    const seg = dayDonutData.find((d) => d.key === key);
    if (!seg) return;
    const mode = key === "needs" && showReviewNav && isToday ? "review" : "presence";
    setDetail({ key, title: seg.name, color: seg.color, rows: seg.rows, mode });
  };
  const goLiveDay = (data?.golive_date || "").slice(0, 10);
  const golMonth = goLiveDay.slice(0, 7);
  const curMonth = todayStr.slice(0, 7);
  const cursorMonth = `${calCursor.y}-${String(calCursor.m).padStart(2, "0")}`;
  const prevDisabled = !!golMonth && cursorMonth <= golMonth;
  const nextDisabled = cursorMonth >= curMonth;
  const shiftMonth = (delta) => setCalCursor((c) => { let m = c.m + delta, y = c.y; if (m < 1) { m = 12; y -= 1; } if (m > 12) { m = 1; y += 1; } return { y, m }; });
  const monthCells = (() => {
    const { y, m } = calCursor;
    const first = new Date(y, m - 1, 1);
    const off = (first.getDay() + 6) % 7;
    const dim = new Date(y, m, 0).getDate();
    const cells = Array(off).fill(null);
    for (let d = 1; d <= dim; d++) cells.push(d);
    return cells;
  })();

  const openReview = (r) => nav(`/attendance/fingerprint?employee=${r.employee_id}&date=${selectedDate || data?.date || ""}`);
  const openDetail = (key) => {
    const map = {
      working: { title: t.working, color: DONUT_COLORS.working, rows: working, mode: "presence" },
      present: { title: t.present, color: "#0058C7", rows: present, mode: "presence" },
      clocked: { title: t.clockedOut, color: DONUT_COLORS.clocked, rows: clockedPure, mode: "presence" },
      not_working: { title: t.notWorking, color: DONUT_COLORS.not_working, rows: notWorking, mode: "presence" },
      not_arrived: { title: t.notArrived, color: DONUT_COLORS.not_arrived, rows: notArrived, mode: "presence" },
      needs: { title: t.needsReview, color: DONUT_COLORS.needs, rows: needsReview, mode: showReviewNav ? "review" : "presence" },
    };
    setDetail({ key, ...map[key] });
  };

  if (!data) return null;
  const preview = showAll ? working : working.slice(0, 6);

  return (
    <section data-testid="live-attendance" className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-[16px] font-semibold tracking-tight">
          <span className="relative flex h-2.5 w-2.5">
            {!before && <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0E8A46] opacity-60" />}
            <span className={`relative inline-flex h-2.5 w-2.5 rounded-full ${before ? "bg-[#C7C7CC]" : "bg-[#0E8A46]"}`} />
          </span>
          {t.liveAtt}
        </h2>
        <button
          data-testid="live-view-attendance"
          onClick={() => nav("/attendance/fingerprint")}
          className={`flex items-center gap-1.5 rounded-full border border-[#E1E1E6] bg-white/70 px-3 py-1.5 text-[12px] font-medium text-[#0071E3] transition-all hover:bg-white ${showReviewNav ? "" : "hidden"}`}
        >
          <Radio className="h-3.5 w-3.5" /> {t.viewAtt}
        </button>
      </div>

      {before && (
        <div data-testid="live-before-golive" className="liquid-glass-card flex items-center gap-3 p-4">
          <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#FFF1E6] text-[#B5590F]">
            <Clock className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <p className="text-[14px] font-semibold text-[#1D1D1F]">{t.beforeGolive}</p>
            <p className="text-[13px] text-[#8E8E93]">
              {t.liveStarts} <span className="font-medium text-[#1D1D1F]">{fmtGoLive(data.golive_date, lng === "id")}</span>.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <SummaryCard testid="live-working" icon={Activity} label={t.working} value={s.currently_working} color="#0E8A46" onClick={() => openDetail("working")} />
        <SummaryCard testid="live-present" icon={Users} label={t.present} value={s.present_today} color="#0058C7" onClick={() => openDetail("present")} />
        <SummaryCard testid="live-clockedout" icon={LogOut} label={t.clockedOut} value={s.clocked_out} color="#6E6E73" onClick={() => openDetail("clocked")} />
        <SummaryCard testid="live-notarrived" icon={UserX} label={t.notArrived} value={notArrived.length} color="#8E8E93" onClick={() => openDetail("not_arrived")} />
        <SummaryCard testid="live-needsreview" icon={AlertTriangle} label={t.needsReview} value={s.needs_review} color="#B5590F" onClick={() => openDetail("needs")} />
      </div>

      {!before && (
        <div className="liquid-glass-card p-5">
          <div className="mb-1 flex items-center justify-between">
            <h3 className="text-[14px] font-semibold tracking-tight">{t.working}</h3>
            {working.length > 6 && (
              <button data-testid="live-view-all" onClick={() => setShowAll((v) => !v)}
                className="flex items-center gap-1 text-[12px] font-medium text-[#0071E3]">
                {showAll ? t.showLess : t.viewAll} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {working.length === 0 ? (
            <p className="py-8 text-center text-[13px] text-[#8E8E93]">{t.noneWorking}</p>
          ) : (
            <div>
              {preview.map((r) => (
                <PreviewRow key={r.employee_id} r={r} expanded={exp[r.employee_id]} t={t} lng={lng}
                  onToggle={() => setExp((e) => ({ ...e, [r.employee_id]: !e[r.employee_id] }))} />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Attendance calendar */}
        <div data-testid="live-attendance-calendar" className="liquid-glass-card p-5">
          <div className="mb-1 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-[#0071E3]" />
            <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{cal.title}</h3>
          </div>
          <p className="mb-3 text-[12.5px] text-[#8E8E93]">{cal.sub}</p>
          <div className="mb-3 flex items-center justify-between">
            <button data-testid="live-cal-prev" onClick={() => shiftMonth(-1)} disabled={prevDisabled}
              className="grid h-8 w-8 place-items-center rounded-full text-[#6E6E73] transition-colors hover:bg-black/[0.05] disabled:opacity-30">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-[14px] font-semibold tracking-tight">{cal.months[calCursor.m - 1]} {calCursor.y}</span>
            <button data-testid="live-cal-next" onClick={() => shiftMonth(1)} disabled={nextDisabled}
              className="grid h-8 w-8 place-items-center rounded-full text-[#6E6E73] transition-colors hover:bg-black/[0.05] disabled:opacity-30">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center">
            {cal.wd.map((w) => (
              <span key={w} className="py-1 text-[11px] font-semibold uppercase tracking-wide text-[#A1A1A6]">{w}</span>
            ))}
            {monthCells.map((d, i) => {
              if (d === null) return <span key={`e${i}`} />;
              const ds = `${calCursor.y}-${String(calCursor.m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
              const disabled = ds > todayStr || (!!goLiveDay && ds < goLiveDay);
              const sel = ds === selectedDate;
              const isT = ds === todayStr;
              return (
                <button
                  key={ds}
                  data-testid={`live-cal-day-${ds}`}
                  disabled={disabled}
                  onClick={() => setSelectedDate(ds)}
                  className={`relative mx-auto grid h-9 w-9 place-items-center rounded-full text-[13px] font-medium transition-all ${
                    sel ? "bg-[#0071E3] text-white shadow-sm"
                      : disabled ? "cursor-not-allowed text-[#C7C7CC]"
                      : "text-[#1D1D1F] hover:bg-[#0071E3]/10"
                  }`}
                >
                  {d}
                  {isT && !sel && <span className="absolute bottom-1 h-1 w-1 rounded-full bg-[#0071E3]" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected-day attendance donut */}
        <div data-testid="live-attendance-donut" className="liquid-glass-card p-5">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <PieIcon className="h-4 w-4 text-[#0071E3]" />
              <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t.attTitle}</h3>
            </div>
            {!isToday && (
              <span className="shrink-0 rounded-full bg-[#EAF0FF] px-2.5 py-1 text-[11.5px] font-semibold text-[#0058C7]">
                {fmtGoLive(selectedDate, lng === "id")}
              </span>
            )}
          </div>
          <p className="mb-3 text-[12.5px] text-[#8E8E93]">{isToday ? t.attSub : t.attPast}</p>
          {!dd ? (
            <div className="grid h-[180px] place-items-center"><Clock className="h-5 w-5 animate-spin text-[#B0B0B8]" /></div>
          ) : (
          <div className="flex flex-col items-center gap-4 sm:flex-row">
            <div className="relative h-[180px] w-[180px] shrink-0">
              {dayDonutTotal === 0 ? (
                <div className="grid h-full w-full place-items-center rounded-full text-center text-[12.5px] text-[#8E8E93]">
                  <span>{dd.before_golive ? t.beforeGoliveShort : t.noData}</span>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={dayDonutData.filter((d) => d.value > 0)} dataKey="value" nameKey="name"
                      innerRadius={56} outerRadius={82} paddingAngle={3} cornerRadius={6} stroke="none"
                      onClick={(seg) => seg?.key && openDayDetail(seg.key)}>
                      {dayDonutData.filter((d) => d.value > 0).map((d) => (
                        <Cell key={d.key} fill={d.color} className="cursor-pointer" />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="w-full space-y-1.5">
              {dayDonutData.map((d) => (
                <button
                  key={d.key}
                  data-testid={`live-donut-${d.key}`}
                  onClick={() => openDayDetail(d.key)}
                  className="flex w-full items-center justify-between rounded-xl px-2.5 py-1.5 text-left transition-colors hover:bg-black/[0.03]"
                >
                  <span className="flex items-center gap-2 text-[13px] font-medium text-[#1D1D1F]">
                    <span className="h-2.5 w-2.5 rounded-sm" style={{ background: d.color }} /> {d.name}
                  </span>
                  <span className="num text-[13px] font-bold">{d.value}</span>
                </button>
              ))}
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 7-day trend */}
      <div data-testid="live-attendance-trend" className="liquid-glass-card p-5">
        <div className="mb-1 flex items-center gap-2">
          <Activity className="h-4 w-4 text-[#0058C7]" />
          <h3 className="text-[13.5px] font-semibold tracking-tight sm:text-[15.5px]">{t.trendTitle}</h3>
        </div>
        <p className="mb-3 text-[12.5px] text-[#8E8E93]">{t.trendSub}</p>
        <div className="h-[240px] w-full">
          {trend.length === 0 ? (
            <div className="grid h-full place-items-center text-center text-[13px] text-[#8E8E93]">
              {before ? t.trendGolive : t.trendEmpty}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={trend} margin={{ top: 8, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="laPresent" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#2C84FF" /><stop offset="100%" stopColor="#0058C7" /></linearGradient>
                  <linearGradient id="laFull" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1FC977" /><stop offset="100%" stopColor="#0C7A3D" /></linearGradient>
                  <linearGradient id="laHalf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#FFB23E" /><stop offset="100%" stopColor="#E8820E" /></linearGradient>
                  <linearGradient id="laNeeds" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D67A24" /><stop offset="100%" stopColor="#B5590F" /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#00000010" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#8E8E93" }} tickLine={false} axisLine={false} width={34} />
                <Tooltip cursor={{ fill: "#00000006", radius: 8 }} contentStyle={{ borderRadius: 12, border: "1px solid #00000010", fontSize: 13, boxShadow: "0 8px 24px #00000014" }} />
                <Legend wrapperStyle={{ fontSize: 11.5 }} />
                <Bar dataKey="present" name={t.legPresent} fill="url(#laPresent)" radius={[5, 5, 0, 0]} maxBarSize={22} />
                <Bar dataKey="full" name={t.legFull} fill="url(#laFull)" radius={[5, 5, 0, 0]} maxBarSize={22} />
                <Bar dataKey="half" name={t.legHalf} fill="url(#laHalf)" radius={[5, 5, 0, 0]} maxBarSize={22} />
                <Bar dataKey="needs" name={t.legNeeds} fill="url(#laNeeds)" radius={[5, 5, 0, 0]} maxBarSize={22} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <DetailSheet
        open={!!detail}
        onClose={() => setDetail(null)}
        title={detail?.title || ""}
        color={detail?.color || "#0071E3"}
        rows={detail?.rows || []}
        mode={detail?.mode}
        onReview={openReview}
        t={t}
        lng={lng}
      />
    </section>
  );
}
